
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Calendar, User, Tag } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { blogPosts } from "./Blog";
import { AdUnit } from "@/components/ads/AdUnit";
import { Helmet } from "react-helmet";

export default function BlogPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const post = blogPosts.find(p => p.id === id);

  if (!post) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Button 
          variant="ghost" 
          className="mb-4"
          onClick={() => navigate('/blog')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Blog
        </Button>
        <h1 className="text-4xl font-cinzel mb-4">Post Not Found</h1>
        <p>Sorry, we couldn't find the blog post you're looking for.</p>
      </div>
    );
  }

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "datePublished": post.date,
    "dateModified": post.date,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://unitcodex.com/blog/${post.id}`
    },
    "publisher": {
      "@type": "Organization",
      "name": "UnitCodex",
      "logo": {
        "@type": "ImageObject",
        "url": "https://unitcodex.com/og-image.png"
      }
    },
    "author": {
      "@type": "Person",
      "name": "UnitCodex Team"
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>{post.title} | UnitCodex Blog</title>
        <meta name="description" content={post.excerpt} />
        <script type="application/ld+json">
          {JSON.stringify(blogSchema)}
        </script>
      </Helmet>
      <div className="flex-grow container mx-auto px-4 py-8 max-w-3xl">
        <Button 
          variant="ghost" 
          className="mb-8"
          onClick={() => navigate('/blog')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Blog
        </Button>

        <article>
          <header className="mb-8">
            <h1 className="text-4xl font-cinzel mb-4">{post.title}</h1>
            <div className="flex items-center gap-2 text-manuscript-primary/60">
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </time>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {post.readTime} min read
              </span>
            </div>
          </header>

          <Card className="bg-manuscript-background/90">
            <CardContent className="p-6 prose prose-manuscript">
              {post.content.split('\n\n').map((paragraph, index) => (
                <p key={index} className="mb-4 last:mb-0 text-manuscript-primary/90">
                  {paragraph}
                </p>
              ))}
            </CardContent>
          </Card>
        </article>

        <div className="mt-12 mb-16">
          <div className="manuscript-divider" />
        </div>

        <div className="my-8">
          <h2 className="text-2xl font-cinzel mb-4">More Stories</h2>
          <div className="grid gap-4">
            {blogPosts
              .filter(p => p.id !== id)
              .slice(0, 2)
              .map(relatedPost => (
                <Card key={relatedPost.id} className="bg-manuscript-background/90">
                  <CardContent className="p-4">
                    <Link to={`/blog/${relatedPost.id}`} className="group">
                      <h3 className="text-xl font-cinzel mb-2 text-manuscript-primary group-hover:text-manuscript-primary/80">
                        {relatedPost.title}
                      </h3>
                      <p className="text-manuscript-primary/80">
                        {relatedPost.excerpt}
                      </p>
                    </Link>
                  </CardContent>
                </Card>
              ))}
          </div>
        </div>
      </div>

      <div className="w-full p-8 mt-8">
        <AdUnit 
          adSlot="3043090605"
          className="min-h-[90px] w-full bg-gray-100/50 rounded-lg"
          adFormat="horizontal"
          desktopOnly
        />
      </div>
    </div>
  );
}
